import { useRef, useState } from 'react'
import { Camera, Loader2, X } from 'lucide-react'
import { errorMessage } from '@/lib/api'
import { ISSUE_PHOTO_ACCEPT, uploadIssuePhoto, type IssuePhoto } from '@/lib/issuePhoto'
import { useToast } from '@/components/ui/toast'

export type { IssuePhoto }

/**
 * The optional picture on a flagged issue, for both the floor and a partner.
 *
 * The file is uploaded as soon as it is picked, the same way artwork is at
 * checkout: the operator sees the thumbnail before committing, and a failed
 * flag does not lose the photo. It stays optional on purpose - a job stuck
 * because nobody could take a picture is worse than a missing one.
 */
export default function IssuePhotoPicker({
  endpoint,
  value,
  onChange,
  disabled = false,
}: {
  /** Portal-specific upload route, e.g. `/staff/issue-photo`. */
  endpoint: string
  value: IssuePhoto | null
  onChange: (photo: IssuePhoto | null) => void
  disabled?: boolean
}) {
  const toast = useToast()
  const input = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  const upload = async (file: File) => {
    setUploading(true)
    try {
      onChange(await uploadIssuePhoto(file, endpoint))
    } catch (err) {
      toast.error('Photo upload failed', errorMessage(err))
    } finally {
      setUploading(false)
      // Clear the input so picking the same file again still fires onChange.
      if (input.current) input.current.value = ''
    }
  }

  if (value) {
    return (
      <div className="mt-1 flex items-start gap-2">
        <a href={value.photo_path} target="_blank" rel="noreferrer" className="shrink-0">
          <img
            src={value.photo_path}
            alt="Attached photo of the problem"
            className="size-20 rounded-lg border border-foreground/15 object-cover"
          />
        </a>
        <button
          type="button"
          onClick={() => onChange(null)}
          className="-m-2 flex size-10 items-center justify-center rounded-lg text-muted hover:bg-danger/10 hover:text-status-danger"
          aria-label="Remove photo"
        >
          <X className="size-4" />
        </button>
      </div>
    )
  }

  return (
    <label
      className={
        disabled || uploading
          ? 'mt-1 flex w-full cursor-default items-center justify-center gap-2 rounded-lg border border-dashed border-foreground/25 px-3 py-3 text-sm font-semibold text-muted'
          : 'mt-1 flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-foreground/25 px-3 py-3 text-sm font-semibold text-primary hover:border-primary/50 hover:bg-primary/5'
      }
    >
      {uploading ? <Loader2 className="size-4 shrink-0 animate-spin" /> : <Camera className="size-4 shrink-0" />}
      {uploading ? 'Uploading...' : 'Add a photo (optional)'}
      <input
        ref={input}
        type="file"
        className="hidden"
        accept={ISSUE_PHOTO_ACCEPT}
        disabled={disabled || uploading}
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) void upload(file)
        }}
      />
    </label>
  )
}
